import _extends from '@babel/runtime/helpers/esm/extends';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { forwardRef } from 'react';
import { useTheme, Box } from '@chakra-ui/react';
import propTypes from 'prop-types';
import merge from 'lodash.merge';

var _excluded = ["size", "leading", "position", "color", "as", "fontSize", "removeMarginInlineStart", "sx"];
var getTextStyles = function getTextStyles(_ref) {
  var theme = _ref.theme,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'md' : _ref$size,
    _ref$leading = _ref.leading,
    leading = _ref$leading === void 0 ? 'base' : _ref$leading,
    _ref$position = _ref.position,
    position = _ref$position === void 0 ? 'relative' : _ref$position;
  if (!theme.typography[size]) {
    throw Error("Unknown size value passed to <Text /> component: ".concat(size, ". Valid values: ").concat(Object.keys(theme.typography).join(', ')));
  }
  if (leading !== 'none' && !(['short', 'base', 'tall'].includes(leading) && theme.typography[size].leading[leading])) {
    throw Error("Unknown leading value passed to <Text /> component: ".concat(leading, ". Valid values: ").concat(Object.keys(theme.typography[size].leading).join(', ')));
  }
  if (leading === 'none') {
    return theme.typography[size].base;
  }
  return merge({
    position: position
  }, theme.typography[size].base, theme.typography[size].leading[leading]);
};
/**
 * ## Truncated text
 *
 * To have truncated text, you must include an inner wrap with `display: block`,
 * and set the `isTruncated` property on it, not on the `<Text>` component:
 *
 * ```
 * <Stack spacing={4}>
 *   <Text size="2xl" isTruncated>❌ The quick brown fox jumped over the lazy dog</Text>
 *   <Text size="2xl" as="div"><Box as="p" isTruncated>✅ The quick brown fox jumped over the lazy dog</Box></Text>
 * </Stack>
 * ```
 *
 * Why? The `<Text>` element has negative top/bottom margin which the descenders
 * and ascenders can go beyond, but if we set `overflow: hidden` on that
 * element, the css is (correctly) cuts them off.
 * By nesting another element inside (which has `display: block`, such as a
 * `<p>` tag), and setting `overflow: hidden` on it, it will not be affected by
 * the parent's negative margin.
 */
var Text = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var size = _ref2.size,
    leading = _ref2.leading,
    _ref2$position = _ref2.position,
    position = _ref2$position === void 0 ? 'relative' : _ref2$position,
    _ref2$color = _ref2.color,
    color = _ref2$color === void 0 ? 'scheme.textPrimary' : _ref2$color,
    _ref2$as = _ref2.as,
    as = _ref2$as === void 0 ? 'p' : _ref2$as,
    fontSize = _ref2.fontSize,
    _ref2$removeMarginInl = _ref2.removeMarginInlineStart,
    removeMarginInlineStart = _ref2$removeMarginInl === void 0 ? false : _ref2$removeMarginInl,
    sx = _ref2.sx,
    props = _objectWithoutProperties(_ref2, _excluded);
  var theme = useTheme();
  var styles = getTextStyles({
    theme: theme,
    size: size || fontSize,
    leading: leading,
    position: position
  });
  return /*#__PURE__*/React.createElement(Box, _extends({
    ref: ref,
    as: as,
    sx: _objectSpread(_objectSpread(_objectSpread({}, styles), sx), {}, {
      marginInlineStart: removeMarginInlineStart ? '0 !important' : 'initial'
    }),
    color: color
  }, props));
});
Text.displayName = 'Text';
Text.propTypes = {
  size: propTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl', '8xl']),
  leading: propTypes.oneOf(['none', 'short', 'base', 'tall']),
  position: propTypes.oneOf(['static', 'relative', 'absolute', 'fixed', 'sticky']),
  color: propTypes.string,
  // as: propTypes.node,
  fontSize: propTypes.oneOfType([propTypes.string, propTypes.number]),
  removeMarginInlineStart: propTypes.bool
  // sx: propTypes.shape(),
};

export { Text, getTextStyles };
